import React, { useContext, useEffect, useState } from "react";
import * as _ from "lodash-es";
import OrderEntryService from "../../../services/OrderEntryService";
import OwButton from "../../shared/OwButton/OwButton";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import UIService from "../../../services/UIService";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IOrder_ItemUI from "../../../models/IOrder_ItemUI";
import { LineItemQtyInventoryDetail } from "../LineItemQtyInventoryDetail/LineItemQtyInventoryDetail";
import { LineItemQtyDeliveryEtaDetail } from "../LineItemQtyDeliveryEtaDetail/LineItemQtyDeliveryEtaDetail";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import IOrderUI from "../../../models/IOrderUI";
import SupplierContext from "../../../contexts/SupplierContext";
import { getPackItemPackCount } from "../../../services/CatalogItemService";

type Props = {
    order: IOrderUI;
    row: IOrder_ItemUI;
    showRowDetailInfo?: boolean;
    updateOrderFn: (order: IOrderUI) => void;
};

export const LineItemQtyEdit = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const getUomSetQtyConversion = (): number | null => {
        let _uomSetQtyConversion: number | null = null;

        if (!_.isNil(props.row.uom_set_qty)) {
            _uomSetQtyConversion = OrderEntryService.getUomSetQty(
                props.row.uom_set_qty,
                props.row.Catalog_Item?.Base_Unit,
                props.row.Selected_Unit
            );
        }

        return _uomSetQtyConversion;
    };

    const [initQty, setInitQty] = useState(props.row.uom_set_qty || props.row.qty);
    const [inputValue, setInputValue] = useState(props.row.uom_set_qty || props.row.qty);
    const [uomSetQtyConversion, setUomSetQtyConversion] = useState<number | null>(getUomSetQtyConversion());

    // Pack-display mode: the buyer edits in whole packs while inputValue stays in units.
    const catalogItem = props.row.Catalog_Item;
    const packQty = catalogItem?.pack_qty || 1;
    const packMode = !!(
        catalogItem?.has_pack_qty &&
        catalogItem?.pack_qty &&
        catalogItem.pack_qty >= 1 &&
        !catalogItem.price_per_pack &&
        !props.row.uom_set_id
    );
    // Snapping happens only on user interaction below, so the stored qty is not mutated on mount.
    const displayValue = packMode && catalogItem ? getPackItemPackCount(catalogItem, inputValue) : inputValue;

    // useEffect(() => {
    //     console.log("renderQtyFn row: ", {...props.row}, "UOM Set Qty: ", props.row.uom_set_qty);
    //     console.log("LineItemQtyEdit Render inputValue:  ", inputValue, "Row Qty: ", props.row.qty, "UoM Set Qty: ", props.row.uom_set_qty);
    // }, []);

    useEffect(() => {
        let updatedInputValue = inputValue;

        const order = props.order!;

        const orderItems = order.Order_Items;

        for (const item of orderItems) {
            if (item.guid === props.row.guid) {
                // console.log("Checking: inputValue: ", inputValue, "currentQty: ", props.row.uom_set_qty,  "initQty: ", initQty);

                const details = OrderEntryService.doInventoryValidation(
                    item.Catalog_Item!,
                    Number(inputValue),
                    order.Order_Items,
                    supplierModel,
                    order,
                    initQty,
                    props.row.Selected_Unit
                );

                // console.log("Qty Should Be: ", details.qty);

                if (!props.row.is_manual_base_qty) {
                    item.qty = details.qty;
                }

                if (!_.isNil(props.row.uom_set_qty)) {
                    item.uom_set_qty = Number(details.qty);

                    if (!props.row.is_manual_base_qty) {
                        item.qty = Number(details.qty) * Number(props.row.Selected_Unit?.conversion_factor);
                    }
                }

                updatedInputValue = details.qty;

                item.isDirty = true;
            }
        }

        order.Order_Items = orderItems;

        // console.log('setInputValue: ', updatedInputValue);

        props.updateOrderFn(order);
        setInputValue(updatedInputValue);
    }, [inputValue]);

    const decrementQty = () => {
        if (packMode && catalogItem) {
            // Step down one whole pack, snapping odd stored quantities to whole packs.
            const decrementVal = (getPackItemPackCount(catalogItem, inputValue) - 1) * packQty;

            if (decrementVal > 0 || supplierModel.allow_neg_order_qty) {
                setInputValue(decrementVal);
            } else {
                setInputValue(packQty);
            }

            return;
        }

        const decrementVal = inputValue - 1;

        if (decrementVal > 0 || supplierModel.allow_neg_order_qty) {
            setInputValue(decrementVal);
        } else {
            setInputValue(1);
        }
    };

    const incrementQty = () => {
        if (packMode && catalogItem) {
            // Step up one whole pack, snapping odd stored quantities to whole packs.
            setInputValue((getPackItemPackCount(catalogItem, inputValue) + 1) * packQty);

            return;
        }

        setInputValue(inputValue + 1);
    };

    const handleQtyChange = (e: any) => {
        const value = e.target.value;

        if (packMode) {
            // The typed number is a pack count; store the unit equivalent.
            setInputValue(Number(value) * packQty);

            return;
        }

        setInputValue(Number(value));
    };

    const getShowSetQtyConversion = (): boolean => {
        let showConversion = true;

        if (supplierModel.hide_uom_base_qty) {
            showConversion = false;
        }

        if (supplierModel.allow_manual_uom_base_qty) {
            showConversion = false;
        }

        return showConversion;
    };

    return (
        <div className={""}>
            <div className={"input-group no-wrap w-10r"}>
                <OwButton
                    centerIconClass={"fas fa-minus"}
                    fn={() => {
                        decrementQty();
                    }}
                    btnSizeClass={" "}
                    disableDblClickPrevention={true}
                />
                <OwFnInput
                    debounceMs={500}
                    enableDebounce={true}
                    defaultClassName={"form-control"}
                    className={"text-end"}
                    fn={(e: any) => {
                        handleQtyChange(e);
                    }}
                    name={`qty`}
                    value={displayValue}
                    type={"number"}
                    onWheelFn={UIService.stopScrollWheel}
                    noOuterDiv={true}
                    // enableDebounce={true}
                />
                <OwButton
                    centerIconClass={"fas fa-plus"}
                    fn={() => {
                        incrementQty();
                    }}
                    btnSizeClass={" "}
                    disableDblClickPrevention={true}
                />
            </div>
            {packMode ? (
                <div className={"text-center text-small"}>
                    <OwLabel title={"packs"} />
                </div>
            ) : null}
            {props.row.uom_set_id ? (
                <div>
                    {uomSetQtyConversion && getShowSetQtyConversion() ? (
                        <div className={"text-center"}>
                            {uomSetQtyConversion} {props.row.Catalog_Item?.Base_Unit?.unit_abbr}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {props.showRowDetailInfo ? <LineItemQtyInventoryDetail row={props.row} /> : null}
            {props.showRowDetailInfo ? <LineItemQtyDeliveryEtaDetail row={props.row} /> : null}
            <div>
                {props.row.Catalog_Item?.min_order_qty ? (
                    <div className={"text-danger text-small text-center"}>
                        <OwLabel title={"min_order_qty"} postText={":"} />
                        {props.row.Catalog_Item?.min_order_qty}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
